import React, { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { Coins, UserPlus, Eye, EyeSlash, ShieldCheck, Lock, Warning } from "@phosphor-icons/react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to={location.state?.from || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("La password deve avere almeno 6 caratteri.");
      return;
    }
    if (password !== password2) {
      setErr("Le due password non corrispondono.");
      return;
    }
    setLoading(true);
    try {
      await register(email.trim().toLowerCase(), password, name.trim());
      // AI works out of the box via the shared Gemini key — go straight home
      navigate("/", { replace: true });
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="inline-flex h-12 w-12 items-center justify-center bg-[#9C5B42] text-white">
            <Coins weight="duotone" size={26} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-3xl text-[#1E2520]">Reperti</div>
            <div className="label-eyebrow">Crea il tuo catalogo</div>
          </div>
        </div>

        <div className="card p-7 md:p-8">
          <h1 className="font-serif text-3xl text-[#1E2520] mb-2">Registrati</h1>
          <p className="text-sm text-[#5C635A] mb-5">
            Crea un account gratuito per iniziare a catalogare i tuoi reperti. Il tuo catalogo
            è privato e isolato — solo tu vedi i tuoi oggetti.
          </p>

          {/* Honest privacy notice: only states things that are actually
              true about the codebase (bcrypt hashing, multi-tenant
              isolation via app, no email verification). */}
          <div className="rounded-md border border-[#E5E0D8] bg-[#F9F8F6] p-4 mb-5 space-y-3" data-testid="privacy-notice">
            <div className="flex gap-3">
              <ShieldCheck weight="duotone" size={22} className="text-[#9C5B42] shrink-0 mt-0.5" />
              <div className="text-sm text-[#1E2520] leading-relaxed">
                <strong>Email finta? Va bene.</strong>{" "}
                <span className="text-[#5C635A]">
                  Non inviamo email di conferma e non chiediamo dati personali.
                  Puoi usare qualsiasi indirizzo (es. <code className="text-[11px] bg-white px-1 py-0.5 rounded-sm">mionick@anonimo.local</code>): serve solo a te per accedere.
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Lock weight="duotone" size={22} className="text-[#9C5B42] shrink-0 mt-0.5" />
              <div className="text-sm text-[#1E2520] leading-relaxed">
                <strong>La password è hashata (bcrypt).</strong>{" "}
                <span className="text-[#5C635A]">
                  Nessuno, neanche il proprietario, può leggerla o recuperarla.
                  È matematicamente irreversibile.
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <ShieldCheck weight="duotone" size={22} className="text-[#9C5B42] shrink-0 mt-0.5" />
              <div className="text-sm text-[#1E2520] leading-relaxed">
                <strong>Il tuo catalogo è isolato.</strong>{" "}
                <span className="text-[#5C635A]">
                  Ogni utente vede solo i propri reperti. Il proprietario, dall'app,
                  vede solo email e numero di reperti per ciascun account — mai i
                  contenuti, le foto o le posizioni dei tuoi ritrovamenti.
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <Warning weight="duotone" size={22} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-[#1E2520] leading-relaxed">
                <strong>Se perdi la password</strong>{" "}
                <span className="text-[#5C635A]">
                  non possiamo recuperarla. Il proprietario può però generarti
                  una password temporanea (da cambiare al primo accesso). Per
                  richiederla, usa il link "Ho perso la password" sulla pagina
                  di accesso.
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-2">Nome (facoltativo)</label>
              <input
                type="text"
                className="input-base"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mario Rossi"
                maxLength={80}
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email"
                className="input-base"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  className="input-base pr-12"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C635A] hover:text-[#1E2520]"
                  aria-label={showPwd ? "Nascondi" : "Mostra"}
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-[#5C635A] mt-1.5">Minimo 6 caratteri.</p>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Conferma password</label>
              <input
                type={showPwd ? "text" : "password"}
                className="input-base"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                autoComplete="new-password"
                required
                minLength={6}
                data-testid="register-password2"
              />
            </div>

            {err && (
              <div className="border border-red-300 bg-red-50 text-red-800 px-4 py-3 rounded-sm text-sm" data-testid="register-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full"
              data-testid="register-submit"
            >
              <UserPlus weight="duotone" size={18} />
              {loading ? "Creazione account..." : "Crea il mio account"}
            </button>
          </form>
        </div>

        <p className="text-sm text-[#5C635A] text-center mt-6">
          Hai già un account?{" "}
          <Link
            to="/login"
            className="text-[#9C5B42] underline font-medium"
            data-testid="register-go-login"
          >
            Accedi
          </Link>
        </p>
        <p className="text-xs text-[#5C635A] text-center mt-3 max-w-sm mx-auto">
          L'identificazione AI funziona subito, senza configurazioni. Se vuoi quote
          più ampie potrai aggiungere una tua chiave Google Gemini gratuita nelle Impostazioni.
        </p>
      </div>
    </div>
  );
}
